import React, { useState } from "react";
import ToolShell from "./ToolShell";
import { Compass } from "lucide-react";
import { postBudget } from "../../lib/api";
import { useLocale } from "../../i18n";

const STYLES = ["budget", "mid", "premium"];

export default function BudgetTool() {
  const { t, locale } = useLocale();
  const [days, setDays] = useState(7);
  const [travelers, setTravelers] = useState(2);
  const [regionCount, setRegionCount] = useState(2);
  const [style, setStyle] = useState("mid");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const data = await postBudget({ days: Number(days), travelers: Number(travelers), style, regions: Number(regionCount) });
      setResult(data);
    } catch (e) { setResult({ error: true }); }
    finally { setLoading(false); }
  };

  const styleLabel = (s) => ({
    budget: locale === "ro" ? "econom" : "budget",
    mid: locale === "ro" ? "mediu" : "mid",
    premium: "premium",
  }[s]);

  const catLabel = (k) => {
    const map = {
      stay: locale === "ro" ? "Cazare" : "Stay",
      food: locale === "ro" ? "Mâncare" : "Food",
      transport: locale === "ro" ? "Transport" : "Transport",
      activities: locale === "ro" ? "Activități" : "Activities",
      intercity_transfers: locale === "ro" ? "Transferuri" : "Inter-city transfers",
    };
    return map[k] || k;
  };

  return (
    <ToolShell
      title={t("tools.budget")}
      subtitle={t("tools.budget_sub")}
      icon={Compass}
      testid="tool-budget"
      seoPath="/tools/budget"
      seoTitle={locale === "ro" ? "Calculator buget călătorie România" : "Romania trip budget calculator"}
      seoDescription={locale === "ro" ? "Estimare realistă a costului călătoriei în România, calibrată pentru prețurile din 2026 pentru budget, mediu și premium." : "Realistic Romania trip cost, calibrated to 2026 prices for budget, mid and premium tiers."}
    >
      <div className="grid lg:grid-cols-12 gap-6">
        <form onSubmit={(e) => { e.preventDefault(); run(); }} className="lg:col-span-5 wr-sharp-card p-8 space-y-6" data-testid="budget-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] block mb-2">{t("common.days")}</label>
              <input type="number" min="1" max="90" value={days} onChange={(e) => setDays(e.target.value)} className="w-full font-mono-wr text-2xl bg-transparent border-0 border-b border-[var(--wr-border)] focus:border-[var(--wr-ink)] py-2 focus:outline-none" data-testid="budget-days" />
            </div>
            <div>
              <label className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] block mb-2">{t("common.travelers")}</label>
              <input type="number" min="1" max="20" value={travelers} onChange={(e) => setTravelers(e.target.value)} className="w-full font-mono-wr text-2xl bg-transparent border-0 border-b border-[var(--wr-border)] focus:border-[var(--wr-ink)] py-2 focus:outline-none" data-testid="budget-travelers" />
            </div>
          </div>
          <div>
            <label className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] block mb-2">{t("common.regions_visited")}</label>
            <input type="number" min="1" max="8" value={regionCount} onChange={(e) => setRegionCount(e.target.value)} className="w-full font-mono-wr text-2xl bg-transparent border-0 border-b border-[var(--wr-border)] focus:border-[var(--wr-ink)] py-2 focus:outline-none" data-testid="budget-regions" />
          </div>
          <div>
            <label className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] block mb-3">{t("common.style")}</label>
            <div className="grid grid-cols-3 gap-1 border border-[var(--wr-border)]" data-testid="budget-style-group">
              {STYLES.map(s => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setStyle(s)}
                  data-testid={`budget-style-${s}`}
                  className={`py-3 font-mono-wr text-[11px] uppercase tracking-widest transition-colors ${
                    style === s ? "bg-[var(--wr-ink)] text-[var(--wr-bg)]" : "text-[var(--wr-ink)] hover:bg-[var(--wr-surface-2)]"
                  }`}
                >{styleLabel(s)}</button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={loading} className="w-full py-3 bg-[var(--wr-ink)] text-[var(--wr-bg)] font-mono-wr text-[11px] uppercase tracking-widest hover:bg-[var(--wr-red)] transition-colors disabled:opacity-50" data-testid="budget-submit">
            {loading ? t("common.loading") : t("common.calculate")}
          </button>
        </form>

        <div className="lg:col-span-7 wr-sharp-card p-8 lg:p-12 bg-[var(--wr-night)] text-[var(--wr-bg)] min-h-[420px]" data-testid="budget-result">
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-white/75">{t("common.result")}</p>
          {result && !result.error && (
            <>
              <p className="font-editorial text-6xl lg:text-7xl mt-4 leading-none tracking-tight">
                {Number(result.total_ron).toLocaleString(locale === "ro" ? "ro-RO" : "en-US", { maximumFractionDigits: 0 })}
                <span className="text-[var(--wr-red)] text-3xl lg:text-4xl ml-3">RON</span>
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 font-mono-wr">
                <div>
                  <p className="text-white/75 text-[10px] uppercase tracking-widest mb-1">{t("common.per_person")}</p>
                  <p className="text-2xl">{Number(result.per_person_ron).toLocaleString(locale === "ro" ? "ro-RO" : "en-US", { maximumFractionDigits: 0 })} <span className="text-sm text-white/80">RON</span></p>
                </div>
                <div>
                  <p className="text-white/75 text-[10px] uppercase tracking-widest mb-1">{t("common.per_day")}</p>
                  <p className="text-2xl">{Number(result.per_day_ron).toLocaleString(locale === "ro" ? "ro-RO" : "en-US", { maximumFractionDigits: 0 })} <span className="text-sm text-white/80">RON</span></p>
                </div>
              </div>
              <div className="mt-8 border-t border-white/15 pt-6 space-y-2 font-mono-wr text-sm">
                {Object.entries(result.breakdown_ron).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-white/10 pb-2">
                    <span className="text-white/80 uppercase text-[10px] tracking-widest">{catLabel(k)}</span>
                    <span className="text-white">{Number(v).toLocaleString(locale === "ro" ? "ro-RO" : "en-US", { maximumFractionDigits: 0 })} RON</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-[12px] text-white/75 leading-relaxed">
                {locale === "ro" ? result.notes_ro : result.notes_en}
              </p>
            </>
          )}
        </div>
      </div>
    </ToolShell>
  );
}
